import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import ContactCompanyInfoForm from "components/form/subcontracting-company/ContactCompanyInfoForm";
import InfoContext from "pages/InfoContext";
import SubcontractingCompanyInfoForm from "components/form/subcontracting-company/SubcontractingCompanyInfoForm";
import ToggleButton from "core/buttons/toggle-button/ToggleButton";
import ValidationModal from "components/modal/ValidationModal";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import { renderInputEmail } from "components/form/CommonInputs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useNavigate } from "react-router-dom";
import { useSettingsContext } from "api/settings/SettingsProvider";
import { useSubcontractorCompanyContext } from "api/directory/subcontractor-company/SubcontractorCompanyProvider";
import { useUserContext } from "api/user/UserProvider";
import { useApiFieldErrors } from "utils/useApiFieldErrors";
import {
  SUBCONTRACTING_COMPANY_DEFAULT_VALUES,
  SUBCONTRACTING_COMPANY_FORM_LABELS,
  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS,
} from "constants/forms";
import colors from "assets/styles/mixins/colors.scss";
import { useLocation } from "react-router-dom";

const CreateSubcontractingCompany = () => {
  const location = useLocation();
  const { subcontractorId } = location.state || {};
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    reset,
    setError,
  } = useForm({
    defaultValues: SUBCONTRACTING_COMPANY_DEFAULT_VALUES,
  });
  const {
    createSubcontractorCompany,
    createdSubcontractorCompany,
    createdSubcontractorCompanyStatus,
  } = useSubcontractorCompanyContext();
  const { userScope, lang } = useUserContext();
  const { dataCountry, getDataCountry } = useSettingsContext();
  const {
    searchDavidsonBySiren,
    sirenData,
    setSirenData,
    sirenErrorMessage,
    setSirenErrorMessage,
  } = useGeneralContext();
  const [isValidated, setIsValidated] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  const partner_connection = watch("partner_connection");
  const [code, setCode] = useState();
  const [status, setStatus] = useState();

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useApiFieldErrors(setError);

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  const onSubmit = async (data) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsOpen(true);
    try {
      await createSubcontractorCompany({
        ...data,
        nationality_id: data?.nationality[0]?.value,
        country: data?.country[0]?.value || data?.country,
        assign_subcontractor: subcontractorId || null,
      });
    } catch {
      // Provider already set createdSubcontractorCompanyStatus to ERROR and apiErrorResponse
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    if (
      createdSubcontractorCompanyStatus === QUERY_STATUS.SUCCESS &&
      createdSubcontractorCompany?.id
    ) {
      setSirenData({});
    }
  }, [createdSubcontractorCompanyStatus, createdSubcontractorCompany]);

  useEffect(() => {
    getDataCountry();
    setSirenData({});
  }, [getDataCountry]);

  useEffect(() => () => setSirenData({}), []);

  const onError = (formErrors) => {
    setStatus(POPUP_STATUS.ERROR);
    const sirenError = formErrors?.siren?.message;
    if (sirenError && typeof sirenError === "string") {
      setCode(sirenError);
    } else {
      setCode(FIELD_REQUIRED);
    }
  };

  const resetForm = () => {
    reset(SUBCONTRACTING_COMPANY_DEFAULT_VALUES);
    setSirenData({});
    setIsValidated(false);
    setCellHighlight(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!partner_connection) setValue("partner_email", null);
  }, [partner_connection]);

  const value = useMemo(
    () => ({
      dataCountry,
      sirenErrorMessage,
      searchDavidsonBySiren,
      setSirenErrorMessage,
      setSirenData,
      sirenData,
      lang,
      setSirenErrorMessage,
      setStatus,
      setCode,
      code,
    }),
    [
      dataCountry,
      sirenData,
      sirenErrorMessage,
      lang,
      dataCountry,
      sirenErrorMessage,
      searchDavidsonBySiren,
      setSirenErrorMessage,
      setSirenData,
      sirenData,
      lang,
      setSirenErrorMessage,
      setStatus,
      setCode,
      code,
    ]
  );

  return (
    <>
      <ValidationModal
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          if (
            createdSubcontractorCompanyStatus === QUERY_STATUS.SUCCESS &&
            createdSubcontractorCompany?.slug
          ) {
            navigate(
              `/${userScope}/subcontractor-company/${createdSubcontractorCompany.slug}`
            );
          }
        }}
        status={createdSubcontractorCompanyStatus}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      <InfoContext.Provider value={value}>
        <AlertPopup
          code={code}
          setCode={setCode}
          setStatus={setStatus}
          status={status}
        />
        <form
          id="create-subcontracting-company-form"
          className="subcontracting-company"
          onSubmit={handleSubmit(onSubmit, onError)}
          style={{ paddingTop: "4.5rem" }}
        >
          <ToggleButton
            id="access_partner_portal"
            label={
              <FormattedMessage
                id="access_partner_portal"
                defaultMessage="Accès au portail partenaire"
              />
            }
            onChange={() => setValue("partner_connection", !partner_connection)}
            value={partner_connection}
          />
          {partner_connection && (
            <div
              className={`email-cell ${
                !watch("partner_email") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputEmail({
                control,
                errors,
                label: SUBCONTRACTING_COMPANY_FORM_LABELS.partner_email,
                placeholder:
                  SUBCONTRACTING_COMPANY_FORM_PLACEHOLDERS.partner_email,
                register,
                name: "partner_email",
              })}
            </div>
          )}
          <div className="subcontracting-company-form">
            <SubcontractingCompanyInfoForm
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              watch={watch}
              control={control}
              register={register}
              errors={errors}
              cellHighlight={cellHighlight}
            />
            <ContactCompanyInfoForm
              setValue={(field, value) => {
                setValue(field, value);
                clearErrors(field);
              }}
              watch={watch}
              control={control}
              register={register}
              errors={errors}
              cellHighlight={cellHighlight}
            />
          </div>
          <div className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              onClick={resetForm}
              type="button"
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              id="button-form-validation"
              color="btn-medium gray-700"
              type="submit"
              onClick={() => setIsValidated(true)}
              backgroundColor={
                createdSubcontractorCompanyStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
              disabled={
                createdSubcontractorCompanyStatus === QUERY_STATUS.LOADING
              }
            >
              <FormattedMessage
                id="button_form_validation"
                defaultMessage="Valider la création"
              />
            </Button>
          </div>
        </form>
      </InfoContext.Provider>
    </>
  );
};

export default CreateSubcontractingCompany;
